import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { Spin, Text } from 'platform-components';
import { DEFAULT_PAGE_TO_LOAD } from 'platform-components/constants';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import { sendHeroElement } from 'platform-components/utils';
import qs from 'query-string';
import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { compose, bindActionCreators, Dispatch } from 'redux';

import { getCurrentClass } from 'common.components/user/selectors';
import { actions as problemsViewActions } from 'store/problems-view/actions';
import hasScrolledToElement from 'utils/has-scrolled-to-element';

import { ThemePageStackContainer as StackComponent } from '../__stack/theme-page__stack.container';
import { ReduxTeacherStore, RootAction } from '../../../typings';
import * as actions from '../../library.store/actions';
import {
    getDeepLinkStack,
    getLastPageLoaded,
    getStacks,
    getProblemsLoadingStatus,
    getSelectedStackId,
    getStacksLoadingStatus,
    getCanLoadMoreStacks,
    getFilterValues,
} from '../../library.store/selectors';
//@ts-ignore
import StackModal from '../../stack-modal/stack-modal';

import './theme-page__stacks.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;

interface DispatchProps {
    loadStacksRequest: typeof actions.loadStacksRequest;
    clearStacks: typeof actions.clearStacks;
    selectStack: typeof actions.selectStack;
    clearAllProblemsAnswers: typeof problemsViewActions.clearAllProblemsAnswers;
    toggleProblemCorrectAnswer: typeof problemsViewActions.toggleProblemCorrectAnswer;
}

interface OwnProps extends WithRouterPathProps {}

interface Props extends StoreProps, DispatchProps, OwnProps {}

interface State {
    stackIndexToOpen: number | null;
}

export class ThemePageStacks extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            stackIndexToOpen: null,
        };
    }

    componentDidMount() {
        // при навигации по темам происходит unmount/mount
        const { stacks, canLoadMoreStacks } = this.props;

        if (!stacks || stacks.length === 0) {
            this.loadStacks();
        }

        if (canLoadMoreStacks) {
            this.bindScroll();
        }

        sendHeroElement();
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps: Props) {
        const {
            canLoadMoreStacks: prevCanLoadMoreStacks,
            stackProblemsLoadingStatus: prevProblemsLoadingStatus,
            themeId: prevThemeId,
            filters: prevFilters,
            grade: prevGrade,
        } = prevProps;
        const { canLoadMoreStacks, loadStacksRequest, stackProblemsLoadingStatus, themeId, filters, grade } =
            this.props;
        const { stackIndexToOpen } = this.state;
        const themeIsChanged = prevThemeId !== themeId;

        if (!isEqual(filters, prevFilters) || themeIsChanged) {
            this.loadStacks();
        }

        if (prevCanLoadMoreStacks && !canLoadMoreStacks) {
            this.unbindScroll();
        }

        if (!prevCanLoadMoreStacks && canLoadMoreStacks) {
            this.bindScroll();
        }

        if (
            themeId === prevThemeId &&
            prevProblemsLoadingStatus === LOADING_STATUSES.LOADING &&
            stackProblemsLoadingStatus === LOADING_STATUSES.SUCCESS &&
            stackIndexToOpen
        ) {
            this.tryOpenStackModal();
        }

        if (
            prevProblemsLoadingStatus === LOADING_STATUSES.LOADING &&
            stackProblemsLoadingStatus === LOADING_STATUSES.SUCCESS &&
            canLoadMoreStacks
        ) {
            this.tryLoadNextStacksPage();
        }

        if (grade !== prevGrade) {
            loadStacksRequest(themeId, DEFAULT_PAGE_TO_LOAD);
        }
    }

    componentWillUnmount() {
        this.unbindScroll();
    }

    lastStack: Element | Text | null;

    bindScroll = () => {
        window.addEventListener('scroll', this.tryLoadNextStacksPage, { passive: true });
        window.addEventListener('resize', this.tryLoadNextStacksPage, { passive: true });
    };

    unbindScroll = () => {
        //@ts-ignore
        window.removeEventListener('scroll', this.tryLoadNextStacksPage, { passive: true });
        //@ts-ignore
        window.removeEventListener('resize', this.tryLoadNextStacksPage, { passive: true });
    };

    loadStacks = () => {
        const { themeId, loadStacksRequest, stacksLoadingStatus } = this.props;

        if (stacksLoadingStatus !== LOADING_STATUSES.LOADING) {
            loadStacksRequest(themeId, DEFAULT_PAGE_TO_LOAD);
        }
    };

    tryLoadNextStacksPage = () => {
        // Проверка isNodeInsideViewport слишком строгая. Она проверяет только верхний край.
        // В то же время может оказаться, что карточка уйдет верхним краем за экран, а нижним - останется внутри.
        // В такой ситуации hasScrolledToElement вернет true, а isInsideViewport - false
        const isScrolledIntoView = hasScrolledToElement(this.lastStack);
        const { stacksLoadingStatus } = this.props;

        if (isScrolledIntoView && stacksLoadingStatus !== LOADING_STATUSES.LOADING) {
            this.loadNextStacksPage();
        }
    };

    loadNextStacksPage = () => {
        const { themeId, loadStacksRequest, lastPageLoaded } = this.props;

        const pageToLoad = isNil(lastPageLoaded) ? DEFAULT_PAGE_TO_LOAD : lastPageLoaded + 1;

        loadStacksRequest(themeId, pageToLoad);
    };

    setLastStackRef = (ref: React.ReactInstance, index: number) => {
        const { stacks } = this.props;

        if (ref && index === stacks.length - 1) {
            this.lastStack = ReactDOM.findDOMNode(ref);
        }
    };

    handleCloseStackModal = () => {
        const { selectStack, clearAllProblemsAnswers, toggleProblemCorrectAnswer } = this.props;

        clearAllProblemsAnswers();
        toggleProblemCorrectAnswer(false);
        selectStack(null);
    };

    handleChangeStack = (stackIndex: number) => {
        const { stacks, selectStack, canLoadMoreStacks, stacksLoadingStatus } = this.props;
        const stack = stacks[stackIndex];

        // когда открываем предпоследнюю стопку из подгруженных - начинаем грузить следующую пачку
        // считам, что при canLoadMoreStacks в списке не меньше 10 стопок
        if (stackIndex === stacks.length - 2 && canLoadMoreStacks) {
            this.loadNextStacksPage();
        }

        if (!stack && stacksLoadingStatus === LOADING_STATUSES.LOADING) {
            this.setState({ stackIndexToOpen: stackIndex });
        }

        if (stack) {
            this.setState({ stackIndexToOpen: null });
            selectStack(stack.id);
        }
    };

    tryOpenStackModal = () => {
        const { stacks, selectStack } = this.props;
        const { stackIndexToOpen } = this.state;
        if (stackIndexToOpen) {
            const stackToOpen = stacks[stackIndexToOpen];

            if (stackToOpen) {
                this.setState({ stackIndexToOpen: null });
                selectStack(stackToOpen.id);
            }
        }
    };

    getSelectedStack = () => {
        const { deepLinkStack, stacks, selectedStackId } = this.props;

        const isDeepLinkStackSelected = deepLinkStack && deepLinkStack.id === selectedStackId;

        return isDeepLinkStackSelected ? deepLinkStack : stacks.find((stack) => stack.id === selectedStackId);
    };

    renderProblems = () => {
        const { stacks, stacksLoadingStatus } = this.props;

        const isLoadingStacks = stacksLoadingStatus === LOADING_STATUSES.LOADING;

        if (!isLoadingStacks && stacks.length === 0) {
            return (
                <div className="theme-page__stacks-empty">
                    <Text id="library.problems.notFound.title" size={Text.SIZE.XXXL} />
                    <Text id="library.problems.notFound.description" size={Text.SIZE.XL} />
                </div>
            );
        }

        return (
            <div className="theme-page__stacks">
                {stacks.map((item, index) => (
                    <StackComponent
                        key={item.id}
                        onProblemRef={(ref: React.ReactInstance) => {
                            this.setLastStackRef(ref, index);
                        }}
                        stack={item}
                    />
                ))}
            </div>
        );
    };

    render() {
        const { stacks, stacksLoadingStatus, canLoadMoreStacks, deepLinkStack } = this.props;
        const { stackIndexToOpen } = this.state;

        const isLoadingStacks = stacksLoadingStatus === LOADING_STATUSES.LOADING;
        const selectedStack = this.getSelectedStack();
        const selectedStackIndex = selectedStack ? stacks.indexOf(selectedStack) : -1;

        const shouldShowStackModal = Boolean(selectedStack && selectedStack.representative);
        const canOpenNextStack = !deepLinkStack && (selectedStackIndex < stacks.length - 1 || canLoadMoreStacks);

        return (
            <>
                {this.renderProblems()}
                {shouldShowStackModal && (
                    <StackModal
                        canOpenNextStack={canOpenNextStack}
                        isLoading={stackIndexToOpen !== null}
                        onClose={this.handleCloseStackModal}
                        onStackChange={this.handleChangeStack}
                        stack={selectedStack}
                        stackIndex={selectedStackIndex}
                    />
                )}
                {isLoadingStacks && (
                    <div className="theme-page__stacks-spin-wrapper">
                        <Spin center />
                    </div>
                )}
            </>
        );
    }
}

const mapStateToProps = (
    storeState: ReduxTeacherStore,
    {
        location: { search },
        match: {
            params: { classId: clId, themeId: thId },
        },
    }: OwnProps
) => {
    const query = qs.parse(search);
    const themeId = Number(thId);
    const classId = Number(clId);
    const currentTeam = getCurrentClass(storeState, { classId });
    const grade = currentTeam?.level || Number(query.grade);

    return {
        canLoadMoreStacks: getCanLoadMoreStacks(storeState, themeId),
        classId,
        deepLinkStack: getDeepLinkStack(storeState),
        lastPageLoaded: getLastPageLoaded(storeState, themeId),
        stackProblemsLoadingStatus: getProblemsLoadingStatus(storeState),
        selectedStackId: getSelectedStackId(storeState),
        stacks: getStacks(storeState, { themeId }) || [],
        stacksLoadingStatus: getStacksLoadingStatus(storeState),
        themeId,
        filters: getFilterValues(storeState),
        grade,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    return bindActionCreators(
        {
            clearStacks: actions.clearStacks,
            loadStacksRequest: actions.loadStacksRequest,
            selectStack: actions.selectStack,
            clearAllProblemsAnswers: problemsViewActions.clearAllProblemsAnswers,
            toggleProblemCorrectAnswer: problemsViewActions.toggleProblemCorrectAnswer,
        },
        dispatch
    );
};

export default compose(withRouterPath, connect(mapStateToProps, mapDispatchToProps))(ThemePageStacks);
